import type { TEvent } from "../types";
import type { TrackingAdapter } from "../subscriber";
import { getAffiliateParams } from "@/AffiliateTracker/affiliate-tracker";

/**
 * Abstract base class for tracking adapters
 */
export abstract class BaseAdapter implements TrackingAdapter {
  /**
   * Name of the tracking adapter
   */
  public abstract readonly name: string;

  /**
   * Configuration for the adapter
   */
  protected config: Record<string, any>;

  /**
   * Whether the adapter has been initialized
   */
  protected initialized = false;

  /**
   * Constructor
   * @param config Configuration for the adapter
   */
  constructor(config: Record<string, any> = {}) {
    this.config = config;
  }

  /**
   * Check if the adapter is enabled
   */
  public isEnabled(): boolean {
    return this.initialized;
  }

  /**
   * Initialize the adapter
   */
  public abstract initialize(): Promise<void>;

  /**
   * Track an event
   * @param event The event to track
   */
  public abstract trackEvent(event: TEvent): Promise<void>;

  /**
   * Get a configuration value
   * @param key The configuration key
   * @param defaultValue The default value if the key is not found
   */
  protected getConfig<T>(key: string, defaultValue?: T): T {
    return key in this.config ? this.config[key] : (defaultValue as T);
  }

  /**
   * Get affiliate parameters from session storage
   * @returns Affiliate parameters or null if not available
   */
  protected getAffiliateParams(): Record<string, string> | null {
    return getAffiliateParams();
  }

  /**
   * Enhance event parameters with affiliate data
   * @param params The original parameters
   * @returns Parameters enhanced with affiliate data
   */
  protected enhanceWithAffiliateParams(
    params: Record<string, any>,
  ): Record<string, any> {
    const affiliateParams = this.getAffiliateParams();
    if (!affiliateParams) return params;

    return {
      ...params,
      // Add affiliate parameters
      utm_source: affiliateParams?.utm_source,
      utm_medium: affiliateParams?.utm_medium,
      utm_campaign: affiliateParams?.utm_campaign,
      utm_term: affiliateParams?.utm_term,
      utm_content: affiliateParams?.utm_content,
      click_id: affiliateParams?.click_id,
      referrer: affiliateParams?.referrer,
    };
  }
}
